import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { AlertCircle, CheckCircle2, PackageCheck } from 'lucide-react'

/**
 * ReviewViewPrototype - Interactive product strain/grade analysis view
 *
 * This prototype demonstrates the new Review Step UX for Import Wizard V3
 * focusing on strain-based product grouping and grade configuration.
 *
 * Layout: Three-panel design
 * - Left: Strain list (with variant counts)
 * - Middle: Grade list for selected strain
 * - Right: Product configuration for selected grade
 */
export default function ReviewViewPrototype({ products = [] }) {
  const [selectedStrain, setSelectedStrain] = useState(null)
  const [selectedGrade, setSelectedGrade] = useState(null)

  /**
   * Analyze products and group by strain with variant detection
   * Handles spelling variations like "Oyster" vs "Oysters" vs "oyster"
   */
  const strainAnalysis = useMemo(() => {
    if (!products || products.length === 0) return []

    // Group products by normalized strain name
    const strainGroups = new Map()

    products.forEach((product, index) => {
      const strain = product.Strain || product.strain || ''
      if (!strain) return

      // Normalize strain name (lowercase, trim)
      const normalizedStrain = strain.toLowerCase().trim()

      if (!strainGroups.has(normalizedStrain)) {
        strainGroups.set(normalizedStrain, {
          normalizedName: normalizedStrain,
          displayName: strain, // Use first occurrence as display name
          variants: new Set([strain]),
          grades: new Map(),
          totalCount: 0
        })
      }

      const group = strainGroups.get(normalizedStrain)
      group.variants.add(strain)
      group.totalCount++

      // Group by grade within strain
      const grade = product['Product Type'] || product.productType || 'Unknown'
      if (!group.grades.has(grade)) {
        group.grades.set(grade, {
          grade,
          products: [],
          isNewProduct: false, // TODO: Check against existing products in DB
          variants: new Set()
        })
      }

      const gradeGroup = group.grades.get(grade)
      gradeGroup.products.push({ ...product, _index: index })
      gradeGroup.variants.add(strain)
    })

    // Convert to array and sort by total count (descending)
    return Array.from(strainGroups.values())
      .sort((a, b) => b.totalCount - a.totalCount)
  }, [products])

  /**
   * Get grade groups for the selected strain
   */
  const gradeGroups = useMemo(() => {
    if (!selectedStrain) return []

    const strain = strainAnalysis.find(
      s => s.normalizedName === selectedStrain.normalizedName
    )

    if (!strain) return []

    return Array.from(strain.grades.values()).sort((a, b) => {
      // Sort by grade name (A Class, B Class, etc.)
      return a.grade.localeCompare(b.grade)
    })
  }, [selectedStrain, strainAnalysis])

  /**
   * Get product details for selected grade
   */
  const selectedGradeDetails = useMemo(() => {
    if (!selectedGrade) return null

    const gradeGroup = gradeGroups.find(g => g.grade === selectedGrade.grade)
    return gradeGroup || null
  }, [selectedGrade, gradeGroups])

  /**
   * Auto-select first strain and grade on load
   */
  useEffect(() => {
    if (strainAnalysis.length > 0 && !selectedStrain) {
      const firstStrain = strainAnalysis[0]
      setSelectedStrain(firstStrain)

      // Auto-select first grade
      if (firstStrain.grades.size > 0) {
        const firstGrade = Array.from(firstStrain.grades.values())[0]
        setSelectedGrade(firstGrade)
      }
    }
  }, [strainAnalysis, selectedStrain])

  /**
   * Handle strain selection
   */
  const handleStrainSelect = (strain) => {
    setSelectedStrain(strain)

    // Auto-select first grade of new strain
    if (strain.grades.size > 0) {
      const firstGrade = Array.from(strain.grades.values())[0]
      setSelectedGrade(firstGrade)
    } else {
      setSelectedGrade(null)
    }
  }

  /**
   * Handle grade selection
   */
  const handleGradeSelect = (grade) => {
    setSelectedGrade(grade)
  }

  // Empty state
  if (!products || products.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <PackageCheck className="w-12 h-12 text-gray-400 mb-4" />
          <p className="text-gray-600 text-center">
            No products to review. Upload a CSV file to get started.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">
          Product Strain Analysis
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Review and configure products grouped by strain and grade
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-semibold text-gray-900">
                {strainAnalysis.length}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Unique Strains
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-semibold text-gray-900">
                {strainAnalysis.reduce((sum, s) => sum + s.grades.size, 0)}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Product Grades
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-semibold text-gray-900">
                {products.length}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                Total Records
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator className="my-6" />

      {/* Three-Panel Layout */}
      <div className="grid grid-cols-12 gap-6 min-h-[500px]">
        {/* Left Panel: Strain List */}
        <div className="col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium">
                Strains ({strainAnalysis.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {strainAnalysis.map((strain) => (
                <button
                  key={strain.normalizedName}
                  onClick={() => handleStrainSelect(strain)}
                  className={`
                    w-full text-left px-3 py-2 rounded-lg border transition-all duration-200
                    ${selectedStrain?.normalizedName === strain.normalizedName
                      ? 'bg-green-50 border-green-200 shadow-sm'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {strain.displayName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {strain.totalCount} record{strain.totalCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {strain.variants.size > 1 && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        {strain.variants.size} variants
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Middle Panel: Grade List */}
        <div className="col-span-4">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium">
                {selectedStrain ? (
                  <>
                    Grades for "{selectedStrain.displayName}"
                  </>
                ) : (
                  'Select a strain'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {!selectedStrain ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">← Select a strain to see grades</p>
                </div>
              ) : gradeGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">No grades found for this strain</p>
                </div>
              ) : (
                gradeGroups.map((gradeGroup) => (
                  <button
                    key={gradeGroup.grade}
                    onClick={() => handleGradeSelect(gradeGroup)}
                    className={`
                      w-full text-left px-3 py-3 rounded-lg border transition-all duration-200
                      ${selectedGrade?.grade === gradeGroup.grade
                        ? 'bg-green-50 border-green-200 shadow-sm'
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {gradeGroup.grade}
                          </span>
                          {gradeGroup.isNewProduct ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              New Product
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              Matched
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {gradeGroup.products.length} record{gradeGroup.products.length !== 1 ? 's' : ''}
                        </div>
                        {gradeGroup.variants.size > 1 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {gradeGroup.variants.size} strain spelling{gradeGroup.variants.size !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel: Product Configuration */}
        <div className="col-span-5">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium">
                {selectedGradeDetails ? (
                  <>Product Configuration</>
                ) : (
                  'Configuration'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedGradeDetails ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">Select a grade to configure</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Product Summary */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Product Details
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Strain:</span>
                        <span className="font-medium text-gray-900">
                          {selectedStrain.displayName}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Grade:</span>
                        <span className="font-medium text-gray-900">
                          {selectedGradeDetails.grade}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Records:</span>
                        <span className="font-medium text-gray-900">
                          {selectedGradeDetails.products.length}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Status:</span>
                        <span>
                          {selectedGradeDetails.isNewProduct ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              New Product
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              Matched
                            </Badge>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Strain Variants */}
                  {selectedGradeDetails.variants.size > 1 && (
                    <>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          Strain Spelling Variants ({selectedGradeDetails.variants.size})
                        </h3>
                        <div className="space-y-1">
                          {Array.from(selectedGradeDetails.variants).map((variant, index) => (
                            <div
                              key={index}
                              className="text-sm text-gray-600 px-3 py-1.5 bg-gray-50 rounded"
                            >
                              "{variant}"
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          These will be normalized to: "{selectedStrain.displayName}"
                        </p>
                      </div>
                      <Separator />
                    </>
                  )}

                  {/* Sample Records */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Sample Records ({Math.min(3, selectedGradeDetails.products.length)} of {selectedGradeDetails.products.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedGradeDetails.products.slice(0, 3).map((product, index) => (
                        <div
                          key={index}
                          className="text-sm border border-gray-200 rounded-lg p-3 bg-white"
                        >
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {product.Quantity} kg
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Price:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                £{product['Price (£/Kg/Unit)']}
                              </span>
                            </div>
                            {product.Customer && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Customer:</span>
                                <span className="ml-2 text-gray-900">
                                  {product.Customer}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedGradeDetails.products.length > 3 && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        + {selectedGradeDetails.products.length - 3} more record{selectedGradeDetails.products.length - 3 !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>

                  <Separator />

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1">
                      Edit Configuration
                    </Button>
                    <Button className="flex-1">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirm
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
